import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgCheveronLeft } from '@actual-app/components/icons/v1';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { color, espacio } from '#components/mobile/ui/tokens';

type MarcoPantallaProps = {
  titulo: ReactNode;
  /** El barco, debajo del título («Personal»). */
  subtitulo: ReactNode;
  onAtras: () => void;
  /** Botón principal, fijo abajo. */
  pie?: ReactNode;
  children: ReactNode;
};

/**
 * Pantalla completa de la tripulación y de «Invitar a alguien»: atrás,
 * título con el barco debajo, contenido con scroll y un pie fijo.
 */
export function MarcoPantalla({
  titulo,
  subtitulo,
  onAtras,
  pie,
  children,
}: MarcoPantallaProps) {
  const { t } = useTranslation();
  return (
    <View
      style={{
        flex: 1,
        height: '100%',
        minHeight: 0,
        backgroundColor: color.bg,
        color: color.fg,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          padding: '10px 8px 6px',
          minHeight: 60,
          flexShrink: 0,
        }}
      >
        <Button
          variant="bare"
          aria-label={t('Back')}
          data-testid="tripulacion-atras"
          onPress={onAtras}
          style={{ width: 44, height: 44, borderRadius: 22, flexShrink: 0 }}
        >
          <SvgCheveronLeft style={{ width: 22, height: 22 }} />
        </Button>
        <View style={{ minWidth: 0, flex: 1 }}>
          <Text
            role="heading"
            aria-level={1}
            style={{
              fontSize: 20,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: color.fg,
            }}
          >
            {titulo}
          </Text>
          <Text
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: color.fg2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitulo}
          </Text>
        </View>
      </View>
      <View
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: `8px ${espacio.margen}px 24px`,
        }}
      >
        <View style={{ gap: 14, flexShrink: 0 }}>{children}</View>
      </View>
      {pie && (
        <View
          style={{
            padding: `10px ${espacio.margen}px`,
            paddingBottom: 'calc(18px + env(safe-area-inset-bottom))',
            borderTop: `1px solid ${color.line}`,
            backgroundColor: color.bg,
            gap: 8,
            flexShrink: 0,
          }}
        >
          {pie}
        </View>
      )}
    </View>
  );
}
